/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

import {
  fetchTournamentLesson,
  fetchTournamentExperiment,
  trackTournamentTimeIncrement,
  startTournamentLesson,
  trackTournamentHintUse,
} from "sj-ui-toolkit/src/api";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { FullScreenLoader } from "sj-ui-toolkit/src/components";

import {
  initLessonCompletedChannel,
  initCodeSubmissionChannel,
  initCtfSubmissionChannel,
} from "sj-ui-toolkit/src/client/actionCable";

const EmptyContextVal = {
  tournamentId: null,
  contentId: null,
  contentType: null,
  content: null,
  contentName: null,
  exerciseContent: null,
  attemptsCount: [],
  maxPoints: 0,
  elapsedTime: 0,
  isLoading: false,
  tournamentLessonNotStarted: false,
  tournamentLessonInProgress: false,
  tournamentLessonPassed: false,
  handleAttempt: () => {},
  beginTournamentLesson: () => {},
  startTimeTracking: () => () => {},
  stopTimeTracking: () => () => {},
  subscribeToChannel: () => {},
  handleHintUsage: async () => {},
};

const TournamentContext = createContext(EmptyContextVal);

export function TournamentContextProvider({
  children,
  tournamentId = null,
  contentId = null,
  contentType = null,
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [contentName, setContentName] = useState("Tournament Content");
  const [exerciseContent, setExerciseContent] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [attemptsCount, setAttemptsCount] = useState([]);
  const [maxPoints, setMaxPoints] = useState(0);
  const tournamentLessonNotStarted = content?.status === "not_started";
  const tournamentLessonInProgress = content?.status === "in_progress";
  const tournamentLessonPassed = content?.status === "passed";

  // Cable subscription ref
  const cableRef = useRef(null);

  // Track time tracking state and cleanup function
  const timeTrackingRef = useRef({ started: false, cleanup: null });

  // Function to reset or set attempt-related state variables (attemptsCount, maxPoints)
  const setAttemptState = (newState = {}) => {
    if (newState.attemptsCount !== undefined) setAttemptsCount(newState.attemptsCount);
    if (newState.maxPoints !== undefined) setMaxPoints(newState.maxPoints);
    if (newState.elapsedTime !== undefined) setElapsedTime(newState.elapsedTime);

    // If called with no arguments, reset attempt values
    if (Object.keys(newState).length === 0) {
      setAttemptsCount([]);
      setMaxPoints(0);
      setElapsedTime(0);
    }
  };

  // Fetch tournament content data
  useEffect(() => {
    const fetchContent = async () => {
      // Return if no identifiers
      if (!tournamentId || !contentId || !contentType) {
        return;
      }

      setIsLoading(true);
      try {
        let data = null;
        let name = "Tournament Content";
        if (contentType === "Lesson") {
          data = await fetchTournamentLesson(contentId);
          setContent(data);
          name = data?.lesson?.lesson?.name || "Lesson";
          if (data.experimentId !== null) {
            const exerciseData = await fetchTournamentExperiment(contentId);
            setExerciseContent(exerciseData);
          }
        } else if (contentType === "Experiment") {
          data = await fetchTournamentExperiment(contentId);
          const { experiment, ...rest } = data;
          setContent(rest);
          setExerciseContent(experiment);
          name = experiment?.experiment?.name || "Experiment";
        }
        setContentName(name);
        setAttemptState({
          attemptsCount: data?.attemptsCount || [],
          maxPoints: data?.maxPoints || 0,
          elapsedTime: data?.seconds || 0,
        });
        // start timetracking immediately if we're resuming an in-progress tournament
        if (data?.status === "in_progress") {
          startTimeTracking();
        }
      } catch (error) {
        console.error("Error fetching tournament content:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [tournamentId, contentId, contentType]);

  // Set breadcrumbs when content is loaded — hook handles unmount cleanup automatically
  useBreadcrumb(
    content && contentName && tournamentId
      ? { label: contentName, route: window.location.pathname }
      : null
  );

  const beginTournamentLesson = useCallback(async () => {
    await startTournamentLesson(contentId);
    startTimeTracking();
  });

  // Tell the context to start tracking time elapsed
  const startTimeTracking = useCallback(() => {
    // Don't start if already tracking
    if (timeTrackingRef.current.started) {
      return;
    }

    timeTrackingRef.current.started = true;

    let elapsedIntervalId = null;
    let trackingIntervalId = null;

    // Update elapsed time every second
    elapsedIntervalId = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    // Call trackTournamentTimeIncrement every 1 second
    trackingIntervalId = setInterval(() => {
      if (contentId && tournamentId) {
        trackTournamentTimeIncrement({ contentType, contentId, seconds: 1 }).catch(err => {
          console.error("Error tracking tournament time:", err);
        });
      }
    }, 1000);

    // Store cleanup function
    timeTrackingRef.current.cleanup = () => {
      if (elapsedIntervalId) clearInterval(elapsedIntervalId);
      if (trackingIntervalId) clearInterval(trackingIntervalId);
      timeTrackingRef.current.started = false;
      timeTrackingRef.current.cleanup = null;
    };
  }, [contentType, contentId, tournamentId]);

  const stopTimeTracking = useCallback(() => {
    if (timeTrackingRef.current.cleanup) {
      timeTrackingRef.current.cleanup();
    }
  }, []);

  // Tell the context to subscribe to the appropriate channel
  const subscribeToChannel = useCallback(channelType => {
    // Unsubscribe from any existing channel
    if (cableRef.current) {
      cableRef.current.unsubscribe();
      cableRef.current = null;
    }

    // Subscribe to the new channel
    switch (channelType) {
      case "ctf":
        cableRef.current = initCtfSubmissionChannel(message => {
          // Update attempts and max points from the message
          setAttemptState({
            attemptsCount: message.attempt.attemptsCount || [],
            maxPoints: message.attempt.maxPoints || 0,
          });
        });
        break;
      case "tests":
        cableRef.current = initCodeSubmissionChannel(message => {
          setAttemptState({
            attemptsCount: message.attempt.attemptsCount || [],
            maxPoints: message.attempt.maxPoints || 0,
          });
        });
        break;
      case "default":
        cableRef.current = initLessonCompletedChannel(message => {
          console.log("Lesson completed message received:", JSON.stringify(message, null, 2));
        });
        break;
      default:
        break;
    }
  }, []);

  // On unmount, clean up any existing cable subscription
  useEffect(() => {
    return () => {
      if (cableRef.current) {
        cableRef.current.unsubscribe();
        cableRef.current = null;
      }
    };
  }, []);

  // Cleanup time tracking on unmount or content change
  useEffect(() => {
    return () => {
      if (timeTrackingRef.current.cleanup) {
        timeTrackingRef.current.cleanup();
      }
    };
  }, [contentId]);

  const handleAttempt = ({ maxPoints, newAttemptsCount }) => {
    setAttemptState({
      attemptsCount: newAttemptsCount,
      maxPoints: maxPoints,
    });
  };

  const handleHintUsage = async (hintId, pointCost) => {
    setMaxPoints(prev => Math.max(prev - pointCost, 10));
    const data = await trackTournamentHintUse({
      contentType,
      contentId,
      hintId,
    });
    return data;
  };

  const value = {
    tournamentId,
    contentId,
    contentType,
    content,
    contentName,
    exerciseContent,
    elapsedTime,
    attemptsCount,
    maxPoints,
    isLoading,
    handleAttempt,
    beginTournamentLesson,
    startTimeTracking,
    stopTimeTracking,
    subscribeToChannel,
    handleHintUsage,
    tournamentLessonNotStarted,
    tournamentLessonInProgress,
    tournamentLessonPassed,
  };

  // If loading, render a loader instead of the children
  if (isLoading) {
    return <FullScreenLoader />;
  }

  return <TournamentContext.Provider value={value}>{children}</TournamentContext.Provider>;
}

export function useTournamentContext() {
  try {
    return useContext(TournamentContext);
  } catch (error) {
    console.error("error using TournamentContext", error);
    return EmptyContextVal;
  }
}
